import { Injectable, OnDestroy } from '@angular/core';
import { NavigationEnd, Router, RouterStateSnapshot, RoutesRecognized, ActivatedRouteSnapshot, Params, UrlTree } from '@angular/router';
import { Title } from '@angular/platform-browser';

import { BehaviorSubject, Observable, Subscription, Subject } from 'rxjs';

import { ViRouteModel } from '../models/vi-route.model';
import { ViRouteType } from '../enums/vi-route-type.enum';
import { LocalizationService } from '../../Core/Localization/services/localization.service';


export interface ViRouteData {
    viRoute?: ViRouteType;
    /** Which param holds the monitor id. Defaults to 'id'. */
    idParam?: string;
    title?: string;
}

@Injectable()
export class ViRoutingService implements OnDestroy {

    private currentRoute: ViRouteModel;
    private currentRouteProvider: BehaviorSubject<ViRouteModel>;

    private navOutsideMonitoring$ = new Subject<null>();

    private recognizedState: RouterStateSnapshot;

    private subscriptions: Subscription[] = [];

    private readonly viRootPath: string = 'visibility-intelligence';

    constructor(
        private router: Router,
        private titleService: Title,
        private localizationService: LocalizationService
    ) {
        this.currentRoute = new ViRouteModel(null);
        this.currentRouteProvider = new BehaviorSubject<ViRouteModel>(this.currentRoute);

        this.subscriptions.push(
            this.router.events.subscribe((e) => {
                // Future state, post-redirect — powers getNextRoute()
                if (e instanceof RoutesRecognized) {
                    this.recognizedState = e.state;
                }

                if (e instanceof NavigationEnd) {
                    const leaf = this.leafOf(this.router.routerState.snapshot);
                    this.updateCurrentRoute(leaf);
                    this.setBrowserTitle(leaf);
                }
            }),
        );

        // Covers the case where this service is created after NavigationEnd fired.
        // Skipped mid-navigation, where routerState still describes the previous page.
        if (this.router.navigated && !this.router.getCurrentNavigation()) {
            this.updateCurrentRoute(this.leafOf(this.router.routerState.snapshot));
        }
    }


    /**
     * Gets an observable for the current route
     * @returns An observable that's updated each time the route changes
     */
    getCurrentRoute(): Observable<ViRouteModel> {
        return this.currentRouteProvider;
    }

    /**
     * The route being navigated to, resolved from the recognized (post-redirect) state.
     * @returns ViRouteModel, or undefined before the first navigation
     */
    getNextRoute(): ViRouteModel {
        return this.recognizedState
            ? this.buildRouteModel(this.leafOf(this.recognizedState))
            : undefined;
    }


    gotoNew() {
        this.goto(['new']);
    }

    gotoEdit(id: number) {
        this.goto([id]);
    }

    gotoRoot() {
        this.goto();
    }

    gotoReports(id: number) {
        this.goto([id, 'results', 'reports']);
    }

    gotoQnA(id: number) {
        this.goto([id, 'results', 'queries-and-answers']);
    }


    hypeUrl(): UrlTree {
        return this.router.parseUrl(this.viRootPath);
    }

    resultsUrl(id: string | number): UrlTree {
        return this.router.parseUrl(`${this.viRootPath}/${id}/results`);
    }

    setupUrl(id: string | number): UrlTree {
        return this.router.parseUrl(`${this.viRootPath}/${id}`);
    }


    private setBrowserTitle(leaf: ActivatedRouteSnapshot): void {
        const data: ViRouteData = leaf.data;

        // Only claim the title on VI routes — DiscoverRoutingService owns its own
        if (data && data.viRoute && data.title) {
            this.titleService.setTitle(this.localizationService.transform(data.title));
        }
    }


    private buildRouteModel(leaf: ActivatedRouteSnapshot): ViRouteModel {
        const data: ViRouteData = leaf.data;

        // No viRoute tag means we're outside Visibility Intelligence
        if (!data || !data.viRoute) {
            return;
        }

        const id = this.paramsOf(leaf)[data.idParam || 'id'];
        const route = new ViRouteModel(data.viRoute, id);

        route.queryParamMap = leaf.queryParamMap;

        return route;
    }

    private updateCurrentRoute(leaf: ActivatedRouteSnapshot): void {
        const route = this.buildRouteModel(leaf);

        if (!route) {
            this.navOutsideMonitoring$.next(null);
            return;
        }

        this.currentRoute = route;
        this.currentRouteProvider.next(this.currentRoute);
    }

    /** Deepest activated route — the one carrying the viRoute tag. */
    private leafOf(state: RouterStateSnapshot): ActivatedRouteSnapshot {
        let node = state.root;

        while (node.firstChild) {
            node = node.firstChild;
        }

        return node;
    }

    /**
     * Params are not inherited past a parent that has both a path and a component,
     * so ':id/results' does not pass 'id' down to its 'reports' child. Merge up the tree.
     */
    private paramsOf(leaf: ActivatedRouteSnapshot): Params {
        return leaf.pathFromRoot.reduce(
            (acc, node) => Object.assign(acc, node.params),
            {} as Params
        );
    }

    /**
     * Routes to a VisibilityIntelligence section
    */
    private goto(path: (string | number)[] = [], replaceURL: boolean = false, queryParams: any = {}) {
        this.router.navigate(
            [
                this.viRootPath,
                ...path
            ],
            // Optionally replace the previous URL in the browser history
            {
                replaceUrl: replaceURL,
                queryParams: queryParams
            }
        );
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}
